import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { getInitials } from "../../../Admin/kReusableCoreTS/SPUserCore";
import { AppIcons } from "./AppIcons";

// AppAvatarFace - what goes INSIDE a contact's disc when no picture covers it: their initials, or
// WhatsApp's own head-and-shoulders silhouette for a contact with no name to take initials from.
// ONE place for BOTH halves - the Bridge imports it the way it imports `AppIcons`:
//   - the service writes it into the script-free contact page's header (`DossierHtml.Portrait`);
//   - the app writes it into every disc of its own (`AppAvatarRules.Disc` / `Small`: the chat list,
//     the task rows, the AI panel's header).
//   `AvatarFace.test.ts` pins the page's disc and the app's disc to this one markup.
//
// ⛔ WHY (seen live 2026-09-27, the chat list): a contact titled "+84944329457" drew an EMPTY grey
// circle - the app's initials rule answers "" for a title that does not start with a letter, and the
// page's took the shared `getInitials` as it is and wrote "+". Neither is a face.
// ⚠ THE SHARED `getInitials` IS NOT WRONG, so it is not changed: it answers the first character of
// each word, which is right for the people it was written for (SharePoint users always have names).
// Deciding that "+", a digit, an emoji's half or "Unknown contact" is not a monogram is THIS product's
// policy, so it lives here.
// ⚠ A second consumer (another product with nameless contacts) would pass its own "no name" label -
// `NoName` is the only WACommander-specific thing in the rule.

export class AppAvatarFace {
    /** The title the page gives a contact with no name and no number (`DossierHtml.NoName`). */
    public static readonly NoName: string = "Unknown contact";

    /**
     * Up to two initials, upper-cased - or "" when the title has none worth drawing: no letter first (a
     * number, an emoji), a raw address (`lid:...`, `...@...`) or the "no name" label.
     */
    public static LettersOf = (Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Text: string = nz(Title).trim();
            if (Text === AppAvatarFace.NoName || Text.indexOf("@") >= 0 || Text.toLowerCase().indexOf("lid:") === 0) { return ""; }
            const Letters: string = getInitials(Text, 2).toUpperCase();
            if (/^\p{L}/u.test(Letters) === false) { return ""; }
            return Letters;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarFace.LettersOf");
            return "";
        }
    };

    /** True when the disc shows the silhouette rather than initials. */
    public static IsNameless = (Title: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return AppAvatarFace.LettersOf(Title).length === 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarFace.IsNameless");
            return false;
        }
    };

    /** The silhouette - a constant of ours, `aria-hidden`, `currentColor`, the full size of its disc. */
    public static Silhouette = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppIcons.Svg(AppIcons.Person, 0, "100%");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarFace.Silhouette");
            return "";
        }
    };

    /** The disc's content: the escaped initials, or the silhouette. Never "" - never an empty circle. */
    public static Inner = (Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Letters: string = AppAvatarFace.LettersOf(Title);
            if (Letters.length === 0) { return AppAvatarFace.Silhouette(); }
            return kHTMLCore.EscapeAttr(Letters);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAvatarFace.Inner");
            return "";
        }
    };
}
